// Recent activity of the connected wallets, and following any intent by its hash.

import { USD, isBasketRow } from "../basket";
import { useEffect, useState } from "react";
import { badgeOf, loadActivity } from "../activity";
import type { IntentRow } from "../api";
import { chainByGumId, knownSrcToken, knownToken, type Chain } from "../chains";
import { prettyUnits, timeAgo } from "../format";
import { isGumDeposit } from "../progress";
import { nativeTokenAddr } from "../wire";
import { ChainLogo, Spinner, TokenOnChain } from "./icons";
import { useNow } from "./hooks";

/** Rows shown before "show more". */
const SHOWN = 5;

type Props = { api: string; owners: string[]; jupnetSrc: Chain | null; current: string | null; bump: number; onOpen: (hash: string) => void };

export function Activity({ api, owners, jupnetSrc, current, bump, onOpen }: Props) {
  const [rows, setRows] = useState<IntentRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [hash, setHash] = useState("");
  const [all, setAll] = useState(false);
  const now = useNow(30_000);
  const sig = owners.join(",");
  useEffect(() => {
    if (!owners.length) return setRows(null);
    let live = true;
    const load = () =>
      loadActivity(api, owners)
        .then((r) => live && (setRows(r), setFailed(false)))
        .catch(() => live && setFailed(true));
    load();
    const t = setInterval(() => !document.hidden && load(), 15_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [api, sig, bump]);
  const valid = /^0x[0-9a-fA-F]{64}$/.test(hash.trim());

  return (
    <section className="card activity" aria-labelledby="activity-h">
      <div className="card-head">
        <h2 id="activity-h">Recent activity</h2>
      </div>
      {!owners.length ? (
        <p className="empty">Connect a wallet to see your swaps here.</p>
      ) : rows === null ? (
        failed ? (
          <p className="empty">Couldn't load your activity. It retries on its own.</p>
        ) : (
          <ul className="activity-list" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="skeleton-row">
                <span className="skeleton circle" />
                <span className="skeleton line" />
              </li>
            ))}
          </ul>
        )
      ) : rows.length ? (
        <ul className="activity-list">
          {(all ? rows : rows.slice(0, SHOWN)).map((r) => (
            <li key={r.intent_hash}>
              <ActivityRow row={r} jupnetSrc={jupnetSrc} now={now} selected={r.intent_hash === current} onOpen={onOpen} />
            </li>
          ))}
          {rows.length > SHOWN && (
            <li>
              <button className="link-btn more" onClick={() => setAll(!all)} aria-expanded={all}>
                {all ? "Show fewer" : `Show ${rows.length - SHOWN} more`}
              </button>
            </li>
          )}
        </ul>
      ) : (
        <p className="empty">No swaps yet from your connected wallets.</p>
      )}
      <form
        className="track-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) {
            onOpen(hash.trim());
            setHash("");
          }
        }}
      >
        <input value={hash} onChange={(e) => setHash(e.target.value)} placeholder="Track an intent by hash (0x…)" aria-label="Intent hash" spellCheck={false} aria-invalid={!!hash && !valid} />
        <button className="btn ghost" disabled={!valid}>
          Track
        </button>
      </form>
    </section>
  );
}

function ActivityRow({ row, jupnetSrc, now, selected, onOpen }: { row: IntentRow; jupnetSrc: Chain | null; now: number; selected: boolean; onOpen: (h: string) => void }) {
  const preset = chainByGumId(row.src_chain_id);
  const src = preset?.family === "jupnet" && jupnetSrc ? jupnetSrc : preset;
  const dst = chainByGumId(row.dst_chain_id);
  const inTok = isBasketRow(row.input_token) ? USD : src ? (src.family === "jupnet" ? knownSrcToken(src, nativeTokenAddr(row.input_token, "jupnet")) : (knownSrcToken(src, nativeTokenAddr(row.input_token, src.family)) ?? src.stable)) : undefined;
  const outTok = dst ? knownToken(dst, nativeTokenAddr(row.dst_token, dst.family)) : undefined;
  const out = row.fulfill_amount ?? row.quoted_dst_amount ?? row.min_dst_amount;
  const gross = BigInt(row.input_amount) + BigInt(row.builder_fee ?? "0") + BigInt(row.protocol_fee ?? "0");
  const badge = badgeOf(row);
  const deposit = isGumDeposit(row);
  return (
    <button className={`activity-row${selected ? " selected" : ""}`} onClick={() => onOpen(row.intent_hash)}>
      <span className="activity-logos">
        {src && inTok ? <TokenOnChain sym={inTok.sym} chain={src.key} size={30} /> : src ? <ChainLogo chain={src.key} size={30} /> : null}
        {dst && outTok ? <TokenOnChain sym={outTok.sym} chain={deposit ? "jupnet" : dst.key} size={30} /> : dst ? <ChainLogo chain={dst.key} size={30} /> : null}
      </span>
      <span className="activity-main">
        <span>
          {inTok ? `${prettyUnits(gross, inTok.decimals)} ${inTok.sym}` : "—"} → {outTok ? `${row.fulfill_amount ? "" : "≈ "}${prettyUnits(out, outTok.decimals)} ${outTok.sym}` : "—"}
        </span>
        <span className="muted">
          {src?.label ?? row.src_chain_id} → {deposit ? "Jupnet" : (dst?.label ?? row.dst_chain_id)} · {timeAgo(Date.parse(row.created_at), now)}
        </span>
      </span>
      <span className={`badge ${badge.tone}`}>
        {badge.tone === "pending" && <Spinner size={11} />}
        {badge.label}
      </span>
    </button>
  );
}
